/**
 * tooltip v2 ランタイム (vanilla, body ポータル方式)
 *
 * 対象: .gp-tooltip.gp-tooltip--v2 (PHP 側でマークアップ済み)
 *
 * 設計:
 *   - bubble を JS で body 直下に **ポータル**する。
 *     これによりサイドバー等の高 z-index / transform 祖先の stacking context に
 *     影響されず、常に最前面に表示できる。
 *   - bubble の位置はトリガーの getBoundingClientRect で動的計算 (top/bottom + 矢印).
 *   - hover / focus / 長押し (タッチ) で表示、ESC で閉じる、外側クリックで閉じる。
 *   - viewport はみ出し時は top <-> bottom を自動反転。
 *
 * 注: trigger は常に body 直下 bubble を 1 つだけ参照する。
 *     mouseenter/leave、focus/blur、touchstart/end で開閉。
 */
(function () {
  'use strict';

  var SELECTOR = '[data-moko-tooltip]';
  var INITED_ATTR = 'data-moko-tooltip-inited';
  var TOUCH_HOLD_MS = 350;
  var HOVER_DELAY_MS = 80;
  var ROOT_CLASS = 'moko-tooltip-portal';
  var ARROW_SIZE = 6;
  var GAP = 8;
  var VIEWPORT_PAD = 8;

  // ---------------------------- ポータル要素 ----------------------------
  var portalEl = null;
  var portalArrowEl = null;
  var portalBodyEl = null;
  var currentOwner = null;
  var holdTimer = null;

  function buildPortal() {
    if (portalEl) return portalEl;
    portalEl = document.createElement('div');
    portalEl.className = ROOT_CLASS;
    portalEl.setAttribute('role', 'tooltip');
    portalEl.setAttribute('hidden', '');
    portalEl.innerHTML =
      '<span class="' + ROOT_CLASS + '__body"></span>' +
      '<span class="' + ROOT_CLASS + '__arrow" aria-hidden="true"></span>';
    document.body.appendChild(portalEl);
    portalBodyEl = portalEl.querySelector('.' + ROOT_CLASS + '__body');
    portalArrowEl = portalEl.querySelector('.' + ROOT_CLASS + '__arrow');
    return portalEl;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ---------------------------- 位置計算 ----------------------------
  // トリガー (.gp-tooltip__trigger) の rect から bubble の位置を決める
  // type: 'top' | 'bottom'。viewport はみ出し時に反転する
  function place(tip) {
    if (!portalEl || !tip) return;

    var trigger = tip.querySelector('.gp-tooltip__trigger');
    if (!trigger) return;
    var tRect = trigger.getBoundingClientRect();

    // bubble サイズ取得用に一度配置 (位置はあとで決める)
    portalEl.style.left = '0px';
    portalEl.style.top = '0px';
    portalEl.style.maxWidth = '280px';
    portalEl.classList.remove('is-flipped');

    var bRect = portalEl.getBoundingClientRect();
    var bw = bRect.width;
    var bh = bRect.height;
    var vw = window.innerWidth;
    var vh = window.innerHeight;

    // 初期 type
    var wantType = tip.classList.contains('gp-tooltip--bottom') ? 'bottom' : 'top';

    // top: trigger 上に置く。 bottom: trigger 下に置く
    var topPos;
    var placedType = wantType;
    if (wantType === 'top') {
      topPos = tRect.top - bh - GAP - ARROW_SIZE;
      if (topPos < VIEWPORT_PAD) {
        // 反転
        topPos = tRect.bottom + GAP + ARROW_SIZE;
        placedType = 'bottom';
      }
    } else {
      topPos = tRect.bottom + GAP + ARROW_SIZE;
      if (topPos + bh > vh - VIEWPORT_PAD) {
        topPos = tRect.top - bh - GAP - ARROW_SIZE;
        placedType = 'top';
      }
    }
    if (placedType === 'bottom') portalEl.classList.add('is-flipped');

    // 水平: trigger 中心に揃え、viewport 内に収める
    var tCenter = tRect.left + tRect.width / 2;
    var leftPos = tCenter - bw / 2;
    if (leftPos < VIEWPORT_PAD) leftPos = VIEWPORT_PAD;
    if (leftPos + bw > vw - VIEWPORT_PAD) leftPos = vw - bw - VIEWPORT_PAD;

    // 矢印の左位置 (bubble に対する相対位置)。trigger 中心を指す
    var arrowLeft = tCenter - leftPos;
    arrowLeft = Math.max(12, Math.min(bw - 12, arrowLeft));

    // スクロール量を足してドキュメント絶対座標へ
    var scrollX = window.scrollX || window.pageXOffset || 0;
    var scrollY = window.scrollY || window.pageYOffset || 0;
    portalEl.style.left = (leftPos + scrollX) + 'px';
    portalEl.style.top  = (topPos + scrollY) + 'px';
    portalArrowEl.style.left = arrowLeft + 'px';
  }

  // ---------------------------- 開閉 ----------------------------
  function show(tip) {
    if (!tip) return;
    buildPortal();
    currentOwner = tip;
    var bubbleNode = tip.querySelector('.gp-tooltip__bubble');
    portalBodyEl.textContent = bubbleNode ? bubbleNode.textContent : '';
    portalEl.removeAttribute('hidden');
    // 1 frame 後に位置決定 → is-open
    requestAnimationFrame(function () {
      place(tip);
      portalEl.classList.add('is-open');
      tip.classList.add('is-open');
    });
  }

  function hide() {
    if (!portalEl) return;
    if (currentOwner) currentOwner.classList.remove('is-open');
    portalEl.classList.remove('is-open');
    var finishHide = function () {
      if (!portalEl.classList.contains('is-open')) {
        portalEl.setAttribute('hidden', '');
        currentOwner = null;
      }
    };
    if (reducedMotion()) finishHide();
    else setTimeout(finishHide, 160);
  }

  // ---------------------------- attach ----------------------------
  function attach(tip) {
    if (tip.getAttribute(INITED_ATTR) === '1') return;
    tip.setAttribute(INITED_ATTR, '1');

    var trigger = tip.querySelector('.gp-tooltip__trigger');
    if (!trigger) return;

    var hoverTimer = null;
    var queueShow = function () {
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { show(tip); }, HOVER_DELAY_MS);
    };
    var queueHide = function () {
      clearTimeout(hoverTimer);
      hide();
    };

    trigger.addEventListener('mouseenter', queueShow);
    trigger.addEventListener('mouseleave', queueHide);
    trigger.addEventListener('focus', function () { show(tip); });
    trigger.addEventListener('blur', queueHide);

    // タッチ: 長押しで開く
    trigger.addEventListener('touchstart', function () {
      clearTimeout(holdTimer);
      holdTimer = setTimeout(function () { show(tip); }, TOUCH_HOLD_MS);
    }, { passive: true });
    trigger.addEventListener('touchend', function () { clearTimeout(holdTimer); });
    trigger.addEventListener('touchmove', function () { clearTimeout(holdTimer); });
    trigger.addEventListener('touchcancel', function () { clearTimeout(holdTimer); });
  }

  // ---------------------------- グローバル ----------------------------
  // ESC で閉じる
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (document.activeElement &&
          document.activeElement.closest &&
          document.activeElement.closest(SELECTOR)) {
        document.activeElement.blur();
      }
      hide();
    }
  });

  // 外側クリックで閉じる
  document.addEventListener('click', function (e) {
    if (!currentOwner) return;
    if (currentOwner.contains(e.target)) return;
    if (portalEl && portalEl.contains(e.target)) return;
    hide();
  });

  // スクロール / リサイズで位置再計算
  var rePlace = function () {
    if (currentOwner && portalEl && !portalEl.hasAttribute('hidden')) {
      place(currentOwner);
    }
  };
  window.addEventListener('scroll', rePlace, { passive: true });
  window.addEventListener('resize', rePlace);

  function scan(scope) {
    var root = scope || document;
    var list = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < list.length; i++) attach(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  document.addEventListener('customDOMContentLoaded', function (event) {
    var modalWindow = event && event.detail && event.detail.modalWindow;
    scan(modalWindow);
  });

  window.MokoTooltipV2 = { scan: scan, show: show, hide: hide };
})();
